import { memo } from "react";
import { FileText, Lock, MoreHorizontal } from "lucide-react";
import { EmbeddedSearchMark } from "@/components/EmbeddedSearchMark";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  DocumentActionsMenuContent,
  type DocumentActionsItem,
} from "@/components/DocumentActionsMenuContent";

export type LibraryDocumentActions = {
  onEdit: (item: DocumentActionsItem) => void;
  onRename: (item: DocumentActionsItem) => void;
  onDuplicate: (item: DocumentActionsItem) => void;
  onMerge: (item: DocumentActionsItem) => void;
  onManageLinks: (item: DocumentActionsItem) => void;
  onReembedForSearch: (item: DocumentActionsItem) => void;
  onEmbedForSearch: (item: DocumentActionsItem) => void;
  onRemoveFromSearch: (item: DocumentActionsItem) => void;
  onToggleIncludeInRag: (item: DocumentActionsItem, value: boolean) => void;
  onMoveToFolder: (item: DocumentActionsItem) => void;
  onRemoveFromFolder: (item: DocumentActionsItem) => void;
  onDelete: (item: DocumentActionsItem) => void;
  onOpenLink?: (item: DocumentActionsItem) => void;
  onViewWorks?: (item: DocumentActionsItem) => void;
  onToggleSourceOfTruth?: (item: DocumentActionsItem) => void;
  onCopyPublicLink?: (item: DocumentActionsItem) => void;
  showCopyPublicLink?: boolean;
};

export interface LibraryDocumentRowProps {
  item: DocumentActionsItem;
  paddingLeft: string;
  embeddingStatusMap?: Record<string, boolean>;
  documentActions?: LibraryDocumentActions | null;
  onDocumentClick: (documentId: string, isLocked?: boolean) => void;
  isSourceOfTruth?: boolean;
}

/** Sidebar library list row: no per-row queries (unlike DocumentItem). */
export const LibraryDocumentRow = memo(function LibraryDocumentRow({
  item,
  paddingLeft,
  embeddingStatusMap,
  documentActions,
  onDocumentClick,
  isSourceOfTruth = false,
}: LibraryDocumentRowProps) {
  const hasMenu = documentActions != null;

  return (
    <div
      className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors min-w-0 group"
      style={{ paddingLeft }}
    >
      <div
        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
        onClick={() => onDocumentClick(item.id, item.isLocked)}
      >
        <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="text-xs truncate flex-1 min-w-0">{item.title}</span>
        {embeddingStatusMap?.[item.id] && <EmbeddedSearchMark className="h-3 w-3" />}
        {item.isLocked && <Lock className="h-3 w-3 text-muted-foreground shrink-0" title="Locked" />}
        <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0">
          {item.itemType === "note" ? "Note" : "Ref"}
        </Badge>
        {isSourceOfTruth && (
          <Badge variant="secondary" className="text-[10px] h-4 px-1 shrink-0" title="Source of Truth">
            Canon
          </Badge>
        )}
      </div>
      {hasMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DocumentActionsMenuContent
            item={item}
            isEmbedded={embeddingStatusMap?.[item.id]}
            onEdit={documentActions.onEdit}
            onRename={documentActions.onRename}
            onDuplicate={documentActions.onDuplicate}
            onMerge={documentActions.onMerge}
            onManageLinks={documentActions.onManageLinks}
            onReembedForSearch={documentActions.onReembedForSearch}
            onEmbedForSearch={documentActions.onEmbedForSearch}
            onRemoveFromSearch={documentActions.onRemoveFromSearch}
            onToggleIncludeInRag={documentActions.onToggleIncludeInRag}
            onMoveToFolder={documentActions.onMoveToFolder}
            onRemoveFromFolder={documentActions.onRemoveFromFolder}
            onDelete={documentActions.onDelete}
            onOpenLink={documentActions.onOpenLink}
            onViewWorks={documentActions.onViewWorks}
            isSourceOfTruth={isSourceOfTruth}
            onToggleSourceOfTruth={documentActions.onToggleSourceOfTruth}
            onCopyPublicLink={documentActions.onCopyPublicLink}
            showCopyPublicLink={
              documentActions.showCopyPublicLink === true && item.itemType === "note"
            }
            stopPropagation={true}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

LibraryDocumentRow.displayName = "LibraryDocumentRow";
